import useDeviceSeriesData from 'features/Integrations/hooks/useDeviceSeriesData';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useGetConnectedDevicesQuery } from 'services/devices/devices.api';
import GraphRenderer from '../../DeviceGraph/Graph/GraphRenderer';
import { useBuoyGraphBuilder } from '../../DeviceGraph/Graph/BuoyGraphBuilder';
import { getBuoyDevices, getFirstAvailableBuoyField } from './buoyGraphUtils';

const MAX_CHART_BUOYS = 5;
const DAY_MS = 24 * 60 * 60 * 1000;

const BuoyWeatherChartPanel = () => {
  const { data: connectedDevices = [] } = useGetConnectedDevicesQuery();
  const chartRef = useRef(null);
  const buoyDevices = useMemo(() => getBuoyDevices(connectedDevices), [connectedDevices]);
  const [unitType, setUnitType] = useState(false);
  const [selectedDataType, setSelectedDataType] = useState('wave_height');

  // `buoyDevices` is a fresh array on every device-cache patch — a socket update
  // for ANY device in the workplace rebuilds it — so the ids the chart shows are
  // keyed on their VALUES, not on that array's identity. Everything derived from
  // them then holds still while the ids themselves do.
  const chartDeviceIdsKey = buoyDevices
    .slice(0, MAX_CHART_BUOYS)
    .map((device) => device.id)
    .join(',');
  const selectedBuoyIds = useMemo(
    () => (chartDeviceIdsKey ? chartDeviceIdsKey.split(',') : []),
    [chartDeviceIdsKey],
  );
  const chartDeviceIds = useMemo(() => selectedBuoyIds.map(Number), [selectedBuoyIds]);

  const selectedDeviceMap = useMemo(() => {
    const map = {};
    selectedBuoyIds.forEach((deviceId) => {
      map[deviceId] = true;
    });
    return map;
  }, [selectedBuoyIds]);

  // The fallback window is frozen at mount, the way DeviceHistory freezes its
  // own: `endTime` is part of the series request key, and a `Date.now()` read on
  // every recompute would move that key — and refetch the whole 24 hours — once
  // per device-cache patch for as long as no buoy holds a cached reading.
  const [mountTime] = useState(() => Date.now());
  const endTime = useMemo(() => {
    const maxTs = Math.max(
      ...buoyDevices.flatMap((device) => (device.data || []).map((point) => point.ts || 0)),
      0,
    );
    return maxTs || mountTime;
  }, [buoyDevices, mountTime]);
  const startTime = endTime - DAY_MS;

  // The page load brings one newest reading per buoy; the 24 hours this chart
  // draws are asked for here, for the buoys it shows.
  const { error: seriesError, loadMore: retrySeries } = useDeviceSeriesData({
    ids: chartDeviceIds,
    startTime,
    endTime,
    limit: 500,
  });
  const defaultField = useMemo(
    () =>
      getFirstAvailableBuoyField({
        devices: connectedDevices,
        selectedDeviceMap,
        unitType,
        startTime,
        endTime,
      }),
    [connectedDevices, selectedDeviceMap, unitType, startTime, endTime],
  );

  useEffect(() => {
    if (defaultField?.value && defaultField.value !== selectedDataType) {
      setSelectedDataType(defaultField.value);
    }
  }, [defaultField, selectedDataType]);

  const graphBuildResult = useBuoyGraphBuilder({
    title: 'Buoy Conditions',
    devices: connectedDevices,
    selectedDeviceMap,
    selectedDataType,
    unitType,
    setUnitType,
    startTime,
    endTime,
    onSelectDataType: setSelectedDataType,
  });

  // A failed range fetch leaves the chart with nothing to draw, and "no data for
  // this field" would be the wrong story to tell: say the readings could not be
  // loaded, carry the backend's own message, and offer the retry the hook keeps
  // callable. A chart built from readings already cached is left alone.
  const renderedResult = useMemo(() => {
    if (!seriesError || graphBuildResult.option) return graphBuildResult;
    return {
      ...graphBuildResult,
      option: null,
      emptyState: {
        title: 'Buoy readings could not be loaded',
        message: seriesError.message,
        detail: 'The last 24 hours of readings were not returned.',
        action: {
          key: 'retry-buoy-series',
          label: 'Try again',
          className: 'px-3 py-2',
          onClick: retrySeries,
        },
      },
    };
  }, [graphBuildResult, seriesError, retrySeries]);

  return (
    <GraphRenderer
      result={renderedResult}
      chartRef={chartRef}
      startTime={startTime}
      endTime={endTime}
    />
  );
};

export default BuoyWeatherChartPanel;
